import React, { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Container,
  Loader,
  Stack,
  Table,
  Tabs,
  Text,
  Title,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import {
  IconCheck,
  IconClipboardCheck,
  IconClockHour4,
} from '@tabler/icons-react';
import { useGetPendingPhysicianEICFConsents } from '../../../global/entities/EICFConsents/queries/useGetPendingPhysicianEICFConsents.tsx';
import { useGetSignedPhysicianEICFConsents } from '../../../global/entities/EICFConsents/queries/useGetSignedPhysicianEICFConsents.tsx';
import { useGetCanSignEICF } from '../../../global/entities/EICFConsents/queries/useGetCanSignEICF.tsx';
import { IEICFConsent } from '../../../global/entities/EICFConsents/EICFConsent.ts';

const EICFPendingSignaturesPage: React.FC = observer(() => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: canSignEICF } = useGetCanSignEICF();
  const { data: pending, isLoading: isLoadingPending } =
    useGetPendingPhysicianEICFConsents(canSignEICF === true);
  const { data: signed, isLoading: isLoadingSigned } =
    useGetSignedPhysicianEICFConsents();

  // Honor a tab requested via navigation state (e.g. "Back to list" from sign page).
  // Controlled state so we can react to a fresh location.state.tab even when
  // React Router preserves the same component instance across navigation.
  const requestedTab = (location.state as { tab?: 'pending' | 'signed' } | null)
    ?.tab;
  const [activeTab, setActiveTab] = useState<'pending' | 'signed'>(
    requestedTab ?? 'pending',
  );
  useEffect(() => {
    if (requestedTab) setActiveTab(requestedTab);
  }, [requestedTab, location.key]);

  useEffect(() => {
    if (canSignEICF === false && activeTab === 'pending') setActiveTab('signed');
  }, [canSignEICF, activeTab]);

  const formatDate = (d?: string | null) =>
    d ? new Date(d).toLocaleString(i18n.language) : '-';

  const templateLabel = (c: IEICFConsent) =>
    `${(i18n.language === 'fr' ? c.templateNameFr : c.templateNameEn) ?? '-'} (v${c.templateVersion})`;

  const renderPending = () => {
    if (isLoadingPending) return <Loader />;
    if (!pending || pending.length === 0)
      {return (
        <Alert
          icon={<IconCheck size="1rem" />}
          color="green"
          title={t('eicf.hcp.noneTitle')}
        >
          {t('eicf.hcp.noneBody')}
        </Alert>
      );}
    return (
      <Table withTableBorder striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('eicf.hcp.patient')}</Table.Th>
            <Table.Th>{t('eicf.hcp.template')}</Table.Th>
            <Table.Th>{t('eicf.hcp.patientSignedDate')}</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {pending.map((c) => (
            <Table.Tr key={c.id}>
              <Table.Td>{c.patientName}</Table.Td>
              <Table.Td>{templateLabel(c)}</Table.Td>
              <Table.Td>{formatDate(c.patientSignedDate)}</Table.Td>
              <Table.Td>
                <Button
                  size="xs"
                  onClick={() =>
                    navigate(`/hcpDashboard/eicfConsent/${c.id}`, {
                      state: { fromTab: 'pending' },
                    })
                  }
                >
                  {t('eicf.hcp.review')}
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  };

  const renderSigned = () => {
    if (isLoadingSigned) return <Loader />;
    if (!signed || signed.length === 0)
      return <Text c="dimmed">{t('eicf.hcp.signedNone')}</Text>;
    return (
      <Table withTableBorder striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('eicf.hcp.patient')}</Table.Th>
            <Table.Th>{t('eicf.hcp.template')}</Table.Th>
            <Table.Th>{t('eicf.hcp.patientSignedDate')}</Table.Th>
            <Table.Th>{t('eicf.hcp.physicianSignedDate')}</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {signed.map((c) => (
            <Table.Tr key={c.id}>
              <Table.Td>{c.patientName}</Table.Td>
              <Table.Td>{templateLabel(c)}</Table.Td>
              <Table.Td>{formatDate(c.patientSignedDate)}</Table.Td>
              <Table.Td>{formatDate(c.physicianSignedDate)}</Table.Td>
              <Table.Td>
                <Button
                  size="xs"
                  variant="default"
                  onClick={() =>
                    navigate(`/hcpDashboard/eicfConsent/${c.id}`, {
                      state: { fromTab: 'signed' },
                    })
                  }
                >
                  {t('eicf.hcp.consult')}
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  };

  const pendingCount = pending?.length ?? 0;
  const signedCount = signed?.length ?? 0;

  return (
    <Container py="lg">
      <Stack gap="md">
        <Title order={2}>{t('eicf.hcp.listTitle')}</Title>
        <Tabs
          value={activeTab}
          onChange={(v) =>
            setActiveTab((v as 'pending' | 'signed') ?? 'pending')
          }
        >
          <Tabs.List>
            {canSignEICF && (
              <Tabs.Tab
                value="pending"
                leftSection={<IconClockHour4 size="1rem" />}
                rightSection={
                  pendingCount > 0 ? (
                    <Badge size="xs" circle color="red">
                      {pendingCount}
                    </Badge>
                  ) : null
                }
              >
                {t('eicf.hcp.tabPending')}
              </Tabs.Tab>
            )}
            <Tabs.Tab
              value="signed"
              leftSection={<IconClipboardCheck size="1rem" />}
              rightSection={
                signedCount > 0 ? (
                  <Badge size="xs" circle color="gray">
                    {signedCount}
                  </Badge>
                ) : null
              }
            >
              {t('eicf.hcp.tabSigned')}
            </Tabs.Tab>
          </Tabs.List>
          {canSignEICF && (
            <Tabs.Panel value="pending" pt="md">
              {renderPending()}
            </Tabs.Panel>
          )}
          <Tabs.Panel value="signed" pt="md">
            {renderSigned()}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
});

export default EICFPendingSignaturesPage;
